<template>
  <el-dialog
    :before-close="cancelDialog"
    :fullscreen="dialogFullscreen"
    :visible.sync="dialog"
    title="获取位置"
    @open="initAddress"
  >
    <div style="padding: 20px 0">
      <div
        v-if="loading"
        style="text-align: center; color: #909399; padding: 40px 0"
      >
        <i class="el-icon-loading" style="font-size: 24px" />
        <p style="margin-top: 12px">定位中，请稍候...</p>
      </div>

      <template v-else>
        <el-form label-width="80px">
          <el-form-item label="详细地址">
            <el-input
              v-model="form.address"
              placeholder="自动获取，可手动修改"
              clearable
              maxlength="50"
            />
          </el-form-item>
          <el-form-item label="经度">
            <el-input v-model="form.lng" placeholder="定位获取" readonly />
          </el-form-item>
          <el-form-item label="纬度">
            <el-input v-model="form.lat" placeholder="定位获取" readonly />
          </el-form-item>
        </el-form>

        <div style="text-align: center; margin-top: 8px">
          <el-button
            type="text"
            icon="el-icon-aim"
            :loading="loading"
            @click="initAddress"
          >
            重新获取当前位置
          </el-button>
        </div>
      </template>
    </div>

    <div slot="footer">
      <el-button @click="cancelDialog">取 消</el-button>
      <el-button type="primary" @click="noDialog">确 定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import dialogMixin from "@/mixin/dialogMixin";
import resDisposeMixin from "@/mixin/resDisposeMixin";

// -------------------------------------------------------
// 天地图 Key（免费）
// 申请：https://umap.tianditu.gov.cn/register.html
// -------------------------------------------------------
const TIANDITU_KEY = "249c959a6044ff1ba048b780131dc030";

const EMPTY_FORM = () => ({
  address: "",
  lng: "",
  lat: "",
  province: "",
  city: "",
  district: "",
});

export default {
  name: "mapDialog",
  mixins: [dialogMixin, resDisposeMixin],
  props: {
    dialog: Boolean,
    deviceId: Number,
  },
  data() {
    return {
      loading: false,
      form: EMPTY_FORM(),
    };
  },
  methods: {
    // -------------------------------------------------------
    // 确定：无坐标时提示，不允许关闭
    // -------------------------------------------------------
    noDialog() {
      if (!this.form.lng || !this.form.lat) {
        this.$message.error(
          "定位失败，无法确认位置，请点击重新获取或检查定位权限"
        );
        return;
      }
      // 与父组件原有监听格式完全一致
      this.refresh.$emit("mapDialog", {
        detail: {
          address: this.form.address,
          location: {
            lat: parseFloat(this.form.lat),
            lng: parseFloat(this.form.lng),
          },
          addressComponents: {
            province: this.form.province,
            city: this.form.city,
            district: this.form.district,
          },
        },
      });
    },

    cancelDialog() {
      this.$emit("update:dialog", false);
    },

    // -------------------------------------------------------
    // 打开弹窗时自动定位
    // -------------------------------------------------------
    async initAddress() {
      this.form = EMPTY_FORM();
      this.loading = true;
      try {
        const coords = await this.getBrowserPosition();
        const detail = await this.reverseGeocode(coords.lng, coords.lat);

        // 逆解析失败会返回 success: false，坐标也不赋值，保持空让确定按钮拦截
        if (!detail.success) {
          this.$message.warning("地址解析失败，请点击重新获取或检查网络");
          return;
        }

        this.form.address = (detail.address || "").slice(0, 50);
        this.form.lng = String(coords.lng);
        this.form.lat = String(coords.lat);
        this.form.province = detail.province;
        this.form.city = detail.city;
        this.form.district = detail.district;
      } catch (err) {
        this.$message.warning(err.message || "定位失败，请检查定位权限后重试");
        // 定位失败时 form 保持 EMPTY_FORM，坐标为空，确定按钮会拦截
      } finally {
        this.loading = false;
      }
    },

    // -------------------------------------------------------
    // 浏览器定位
    // -------------------------------------------------------
    getBrowserPosition() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          return reject(new Error("当前浏览器不支持定位"));
        }
        navigator.geolocation.getCurrentPosition(
          (pos) =>
            resolve({ lng: pos.coords.longitude, lat: pos.coords.latitude }),
          (err) => {
            const messages = {
              1: "定位授权被拒绝，请在浏览器设置中允许获取位置",
              2: "无法获取位置信息，请检查网络或设备 GPS",
              3: "定位超时，请重试",
            };
            reject(new Error(messages[err.code] || "定位失败"));
          },
          { timeout: 10000, enableHighAccuracy: true }
        );
      });
    },

    // -------------------------------------------------------
    // 天地图逆地址解析（免费）
    // 返回 address / province / city / district
    // -------------------------------------------------------
    async reverseGeocode(lng, lat) {
      try {
        const postStr = JSON.stringify({ lon: lng, lat, ver: 1 });
        const url =
          `https://api.tianditu.gov.cn/geocoder?postStr=${encodeURIComponent(
            postStr
          )}` + `&type=geocode&tk=${TIANDITU_KEY}`;
        const res = await fetch(url);
        const data = await res.json();
        const result = data?.result || {};
        const comp = result.addressComponent || {};

        // 天地图直辖市（北京/上海/重庆/天津）city 字段为空，用 province 兜底
        const province = comp.province || "";
        const city = comp.city || province;
        // 天地图区/县对应 county 字段
        const district = comp.county || "";

        return {
          success: true,
          address: result.formatted_address || "",
          province,
          city,
          district,
        };
      } catch {
        return {
          success: false,
          address: "",
          province: "",
          city: "",
          district: "",
        };
      }
    },
  },
};
</script>
